import React, {FC, useCallback, useEffect, useState} from 'react';
import {FiArrowRightCircle} from 'react-icons/fi';
import { Link, useHistory } from 'react-router-dom';
import { IconType } from 'react-icons';
import { useDispatch, useSelector } from 'react-redux';
// custom
import NavbarUser from '../../components/navbar/Navbar.user';
import { IUserState } from '../../common/state/profile/user';
import { getAllPayments, getEvents, getPaymentSignature } from '../../common/async/AsyncCalls';
import SimpleSpinner from '../../components/loaders/spinners/SimpleSpinner';
import { EVENT_ACTION_TYPE, IEvent } from '../../common/state/events/event';
import { IPayment, PAYMENT_ACTION_TYPE } from '../../common/state/payments/payment';
import { toast } from 'react-toastify';
import { GENERAL_INFO_ACTION_TYPE } from '../../common/state/subscriptions/general_info';

type IFeatures = {
    name: string,
    description: string,
    icon: IconType,
    link: string,
    type: 'a' | 'l' | 'b'
};

const UserView : FC = () => {
    // history
    const history = useHistory();
    // states
    const token = useSelector(({user} : {
        user: IUserState
    }) => user.token);
    const [isLoading, toggleLoading] = useState(true);
    // actions
    const eventDispatch : (data : {type: EVENT_ACTION_TYPE, payload: any}) => void = useDispatch();
    const paymentDispatch: (data : {type: PAYMENT_ACTION_TYPE, payload: any}) => void = useDispatch();
    const generalInfoDispatch: (data: {type: GENERAL_INFO_ACTION_TYPE, payload: any}) => void = useDispatch();
    
    // lifecycle
    const fetchEventsPaymentsInfo = useCallback(async ()=>{
        if (!token || token === '') return;
        const promiseList = [getEvents(), getAllPayments(), getPaymentSignature(token)];
        try {
            toggleLoading(true);
            const responseList = await Promise.all(promiseList);
            const [eventResponse, paymentResponse, vendorSignatureResponse] = responseList;
            const signature = vendorSignatureResponse?.data?.stripe_key;
            const eventDetails = eventResponse?.data as IEvent[];
            const paymentDetails = paymentResponse?.data as IPayment[];
            // active events
            const activeEventDetails = eventDetails.filter((item: IEvent) => item.active === true);
            // update states
            paymentDispatch({type: PAYMENT_ACTION_TYPE.VENDOR_SIGNATURE, payload: signature});
            eventDispatch({type: EVENT_ACTION_TYPE.EVENT_LIST_UPDATE, payload: activeEventDetails});
            paymentDispatch({type: PAYMENT_ACTION_TYPE.PAYMENT_LIST_UPDATE, payload: paymentDetails});
            toast('Fetched information');
        } catch (err) {
            toast('Error fetching information!');
            console.log('Error fetching events, payments: ', err);
        } finally {
            toggleLoading(false);
        }

    },[token]);
    
    // is loaded
    useEffect(()=>{
        toggleLoading(true);
        // check if redirected from payment        
        const params = new URLSearchParams(window.location.search);
        const payment_intent = params.get('payment_intent');
        const payment_secret = params.get('payment_intent_client_secret');
        const redirect_status = params.get('redirect_status');        
        // if payment route - check redirect status
        if (payment_intent && payment_secret && redirect_status) {
            if (redirect_status === 'succeeded') history.push({pathname: '/payment_success'});
            else history.push({pathname: '/payment_failure'});
        }
        fetchEventsPaymentsInfo();
        generalInfoDispatch({type: GENERAL_INFO_ACTION_TYPE.INFO_RESET, payload: {}});
    },[]);

    const features: IFeatures[] = [
        {
            name: 'Upcoming Events',
            description: 'Click here to view and register upcoming events.',
            icon: FiArrowRightCircle,
            link: '/profile/events',
            type: 'l'
        }, {
            name: 'View My Profile',
            description: 'Click here to view your profile information and your subscription status',
            icon: FiArrowRightCircle,
            link: '/profile/details',
            type: 'l'
        }, {
            name: 'Contact Us',
            description: `We've come up with a simple-easy to use form that you can fill in to reach out to use`,
            icon: FiArrowRightCircle,
            link: '/profile/contact',
            type: 'l'
        }, {
            name: 'Our Website',
            description: 'Visit our parent website for more information about Power Ministries',
            icon: FiArrowRightCircle,
            link: 'https://www.powerministry.info',
            type: 'a'
        }
    ];
    return <React.Fragment>
        <NavbarUser/>
        <main
            className="flex flex-col items-center justify-start h-screen overflow-y-scroll text-gray-700 bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dartext-base">
            <div className="py-10 pb-24 mt-2 mb-4 text-gray-900 bg-gray-200 rounded-lg shadow-lg dark:bg-gray-700 dark:text-white">
                <div className="px-6 mx-auto max-w-7xl lg:px-8">
                    <div className="sm:text-center">                        
                        <h2 className="text-3xl font-bold tracking-tight text-blue-800 dark:text-teal-200 sm:text-4xl">Power Ministries International</h2>
                        <p className="text-2xl font-bold tracking-tight text-blue-800 dark:text-teal-200 sm:text-4xl">Event Registration Application</p>
                        <p className="max-w-2xl mx-auto mt-6 text-lg leading-8">
                            Welcome to your dashboard. Here you can subscribe to upcoming events, view and download event related documents, brochures
                            or you can reach out to us using the &quot;Contact Us&quot; form.
                        </p>
                    </div>

                    <div className="max-w-lg mt-20 sm:mx-auto md:max-w-none">
                        <div
                            className="grid grid-cols-1 gap-y-16 md:grid-cols-2 md:gap-x-12 md:gap-y-16">
                            {features.map((feature) => (
                                <section className="relative flex flex-col" key={feature.name}>                                    
                                <div
                                    key={feature.name}
                                    className="relative flex flex-col gap-6 sm:flex-row md:flex-col lg:flex-row">
                                    {feature.type === 'l' ? <Link to={feature.link}
                                        className="flex items-center justify-center w-12 h-12 text-white bg-indigo-500 rounded-xl dark:bg-teal-600 sm:shrink-0">
                                        <feature.icon className="w-8 h-8" aria-hidden="true"/>
                                    </Link> : feature.type === 'a' ? <a href={feature.link} target="_blank" rel="noopener noreferrer"
                                        className="flex items-center justify-center w-12 h-12 text-white bg-indigo-500 rounded-xl dark:bg-teal-600 sm:shrink-0">
                                        <feature.icon className="w-8 h-8" aria-hidden="true"/>
                                    </a>: <button
                                        className="flex items-center justify-center w-12 h-12 text-white bg-indigo-500 rounded-xl dark:bg-teal-600 sm:shrink-0">
                                        <feature.icon className="w-8 h-8" aria-hidden="true"/>
                                    </button>}
                                    <div className="sm:min-w-0 sm:flex-1">
                                        <p className="text-lg font-semibold leading-8 text-blue-800 dark:text-teal-200">{feature.name}</p>
                                        <p className="mt-2 text-base leading-7">{feature.description}</p>
                                    </div>                                                                        
                                </div>                                
                                </section>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <SimpleSpinner loading={isLoading} title={'Fetching additional details...'}/>
        </main>
    </React.Fragment>
};

export default UserView;
